/**
 * Audit Plan Service (EX9)
 * 
 * Handles all audit planning related API calls including:
 * - Testing Timeline (Tab 1)
 * - Planning Documents (Tab 2)
 * - Applications (Tab 3)
 * - Change Indicator Checklist (Tab 4)
 */

import { apiRequest } from './apiClient';
import { SyncfusionGridParams, RiskRanking, RiskRankingApi, EmptyResponse, SyncfusionSearchParam } from './types';
import { parseCommaSeparated } from '../utils/stringUtils';

// ========================================
// Tab 1: Testing Timeline Types
// ========================================

export interface TimelineControlBackend {
  Id: string;
  ControlCode: string;
  CycleId: string;
  CycleCode: string;
  CycleName: string;
  RiskRanking: string;
  RiskRatingRaw: number;
  TestingStatus: number;
  What: string;
  When: string;
  Who: string;
  Why: string;
  How: string;
  Evidence: string;
  AuditTimeSlots: TimelineTimeslotBackend[];
  CreatedAt: string;
}

export interface TimelineTimeslotBackend {
  AuditTimeslotId: string;
  AuditorId: string;
  AuditorName: string;
  StartDate: string;
  EndDate: string;
}

export interface TimelineControl {
  id: string;
  controlCode: string;
  cycle: {
    id: string;
    code: string;
    name: string;
  };
  riskRanking: RiskRanking;
  testingStatus: number;
  controlDescription: {
    what: string;
    when: string;
    who: string;
    why: string;
    how: string;
    evidence: string;
  };
  timeslots: TimelineTimeslot[];
  createdAt: string;
}

export interface TimelineTimeslot {
  id: string;
  auditorId: string;
  auditorName: string;
  startDate: string;
  endDate: string;
}

export interface TimelineCycleGroup {
  cycleId: string;
  cycleCode: string;
  cycleName: string;
  controls: TimelineControl[];
}

// Timeslot API Types
export interface CreateTimeslotRequest {
  auditorId: string;
  controlId: string;
  startDate: string; // ISO date
  endDate: string;   // ISO date
}

export interface UpdateTimeslotRequest extends CreateTimeslotRequest {
  auditTimeslotId: string;
}

// ========================================
// Tab 2: Planning Documents Types
// ========================================

export interface PlanningDocumentBackend {
  Id: string;
  FileName: string;
  FileSize: number;
  UploadedByName: string;
  CreatedAt: string;
}

export interface PlanningDocument {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedBy: string;
  uploadedAt: string;
}

// ========================================
// Tab 3: Applications Types
// ========================================

export interface ApplicationBackend {
  Id: string;
  Index: number;
  Name: string;
  IsInScope: boolean;
  RiskRanking: string;
  RiskRankingRaw: number;
  SystemAdmin: string;
  Description: string;
  ProcessIds: string;
  ProcessNames: string;
  ControlIds: string;
  ControlNames: string;
  CreatedAt: string;
}

export interface Application {
  id: string;
  index: number;
  name: string;
  auditScope: 'In Scope' | 'Out of Scope';
  riskRanking: RiskRanking;
  systemAdministrator: string;
  description: string;
  auditorNotes?: string;
  processes: Array<{ id: string; name: string }>;
  controlActivities: Array<{ id: string; name: string }>;
  linkedControlsCount: number;
  createdAt: string;
}

export interface CreateApplicationRequest {
  annualReportId: string;
  index: number;
  name: string;
  isInScope: boolean;
  riskRating: RiskRankingApi;
  systemAdmin: string;
  description?: string;
  auditorsNotes?: string;
  controlIds?: string[];
}

export interface UpdateApplicationRequest extends CreateApplicationRequest {
  auditApplicationId: string;
}

// ========================================
// Tab 4: Change Indicator Checklist Types
// ========================================

export interface ChangeIndicatorBackend {
  Id: string;
  Quarter: string;
  ProcessOwnerId: string;
  ProcessOwnerName: string;
  Question: string;
  Response: string;
  ChecklistKeyChange: string;
  ChecklistKeyChangeRaw: number;
  IsAcknowledged: boolean;
  ProcessIds: string;
  ProcessNames: string;
  CreatedAt: string;
}

export interface ChangeIndicator {
  id: string;
  quarter: string;
  processOwnerName: string;
  processes: Array<{ id: string; name: string }>;
  question: string;
  response: string;
  significantChange: 'changeIdentified' | 'noChanges' | 'na';
  status: 'pending' | 'read';
  createdAt: string;
}

export interface CertificationMessageBackend {
  Id: string;
  Quarter: string;
  ProcessOwnerId: string;
  ProcessOwnerName: string;
  Message: string;
  CertificationCommentType: string;
  CertificationCommentTypeRaw: number;
  IsAcknowledged: boolean;
  ProcessIds: string;
  ProcessNames: string;
  CreatedAt: string;
}

export interface CertificationMessage {
  id: string;
  quarter: string;
  processOwnerName: string;
  processes: Array<{ id: string; name: string }>;
  section: string;
  message: string;
  status: 'pending' | 'read';
  createdAt: string;
}

// ========================================
// Backend Response Types
// ========================================

interface TimelineGridResponse {
  result: TimelineControlBackend[];
  count: number;
}

interface DocumentsGridResponse {
  result: PlanningDocumentBackend[];
  count: number;
}

interface ApplicationsGridResponse {
  result: ApplicationBackend[];
  count: number;
}

interface ChangeIndicatorGridResponse {
  result: ChangeIndicatorBackend[];
  count: number;
}

interface CertificationMessageGridResponse {
  result: CertificationMessageBackend[];
  count: number;
}

// ========================================
// Mapping Functions
// ========================================

const mapRiskRanking = (ranking: string): RiskRanking => {
  const normalized = ranking?.toLowerCase() || '';
  if (normalized === 'high') return 'High';
  if (normalized === 'med') return 'Med';
  if (normalized === 'low') return 'Low';
  return 'Unranked';
};


const mapSignificantChange = (value: string): ChangeIndicator['significantChange'] => {
  const normalized = value?.toLowerCase() || '';
  if (normalized === 'changeidentified' || normalized === 'change_identified') return 'changeIdentified';
  if (normalized === 'nochanges' || normalized === 'no_changes') return 'noChanges';
  return 'na';
};

/**
 * Map backend timeline control to frontend format
 */
const mapTimelineControl = (backend: TimelineControlBackend): TimelineControl => {
  return {
    id: backend.Id,
    controlCode: backend.ControlCode,
    cycle: {
      id: backend.CycleId,
      code: backend.CycleCode,
      name: backend.CycleName || '',
    },
    riskRanking: mapRiskRanking(backend.RiskRanking),
    testingStatus: backend.TestingStatus || 0,
    controlDescription: {
      what: backend.What || '',
      when: backend.When || '',
      who: backend.Who || '',
      why: backend.Why || '',
      how: backend.How || '',
      evidence: backend.Evidence || '',
    },
    timeslots: (backend.AuditTimeSlots || []).map(ts => ({
      id: ts.AuditTimeslotId,
      auditorId: ts.AuditorId,
      auditorName: ts.AuditorName || '',
      startDate: ts.StartDate,
      endDate: ts.EndDate,
    })),
    createdAt: backend.CreatedAt || '',
  };
};

/**
 * Map backend document to frontend format
 */
const mapPlanningDocument = (backend: PlanningDocumentBackend): PlanningDocument => {
  // Extract file type from filename extension
  const fileType = backend.FileName.split('.').pop()?.toLowerCase() || 'unknown';
  
  return {
    id: backend.Id,
    fileName: backend.FileName,
    fileType: fileType,
    fileSize: backend.FileSize || 0,
    uploadedBy: backend.UploadedByName || 'Unknown',
    uploadedAt: backend.CreatedAt,
  };
};

/**
 * Map backend application to frontend format
 */
const mapApplication = (backend: ApplicationBackend): Application => {
  const processIds = parseCommaSeparated(backend.ProcessIds);
  const processNames = parseCommaSeparated(backend.ProcessNames);
  const controlIds = parseCommaSeparated(backend.ControlIds);
  const controlNames = parseCommaSeparated(backend.ControlNames);

  return {
    id: backend.Id,
    index: backend.Index,
    name: backend.Name,
    auditScope: backend.IsInScope ? 'In Scope' : 'Out of Scope',
    riskRanking: mapRiskRanking(backend.RiskRanking),
    systemAdministrator: backend.SystemAdmin || '',
    description: backend.Description || '',
    auditorNotes: '', // Not in API response
    processes: processIds.map((id, i) => ({
      id,
      name: processNames[i] || '',
    })),
    controlActivities: controlIds.map((id, i) => ({
      id,
      name: controlNames[i] || '',
    })),
    linkedControlsCount: controlIds.length, // Compute from control IDs
    createdAt: backend.CreatedAt,
  };
};

/**
 * Map backend change indicator to frontend format
 */
const mapChangeIndicator = (backend: ChangeIndicatorBackend): ChangeIndicator => {
  const processIds = parseCommaSeparated(backend.ProcessIds);
  const processNames = parseCommaSeparated(backend.ProcessNames);

  return {
    id: backend.Id,
    quarter: backend.Quarter,
    processOwnerName: backend.ProcessOwnerName || '',
    question: backend.Question || '',
    response: backend.Response || '',
    significantChange: mapSignificantChange(backend.ChecklistKeyChange),
    status: backend.IsAcknowledged ? 'read' : 'pending',
    createdAt: backend.CreatedAt,
    processes: processIds.map((id, i) => ({
      id,
      name: processNames[i] || '',
    })).sort((a, b) => a.name.localeCompare(b.name)),
  };
};

/**
 * Map backend certification message to frontend format
 */
const mapCertificationMessage = (backend: CertificationMessageBackend): CertificationMessage => {
  const processIds = parseCommaSeparated(backend.ProcessIds);
  const processNames = parseCommaSeparated(backend.ProcessNames);

  return {
    id: backend.Id,
    quarter: backend.Quarter || '',
    processOwnerName: backend.ProcessOwnerName || '',
    section: backend.CertificationCommentType || '',
    message: backend.Message || '',
    status: backend.IsAcknowledged ? 'read' : 'pending',
    createdAt: backend.CreatedAt,
    processes: processIds.map((id, i) => ({
      id,
      name: processNames[i] || '',
    })).sort((a, b) => a.name.localeCompare(b.name)),
  };
};

// ========================================
// Tab 1: Testing Timeline API Functions
// ========================================

export interface GetTimelineControlsRequest {
  annualReportId: string;
  skip?: number;
  take?: number;
}

export interface GetTimelineControlsResponse {
  data: TimelineControl[];
  groupedByCycle: TimelineCycleGroup[];
  totalCount: number;
}

/**
 * Get all timeline controls for an annual report
 */
export const getTimelineControls = async (
  request: GetTimelineControlsRequest
): Promise<GetTimelineControlsResponse> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
    };

    const response = await apiRequest<TimelineGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/index-grid/timeline/${request.annualReportId}`,
      requestBody,
      true
    );

    const mappedData = response.result.map(mapTimelineControl);

    // Group by cycle
    const cycleMap = new Map<string, TimelineCycleGroup>();
    
    mappedData.forEach(control => {
      const cycleKey = control.cycle.id;
      if (!cycleMap.has(cycleKey)) {
        cycleMap.set(cycleKey, {
          cycleId: control.cycle.id,
          cycleCode: control.cycle.code,
          cycleName: control.cycle.name,
          controls: [],
        });
      }
      cycleMap.get(cycleKey)!.controls.push(control);
    });

    // Sort cycles by code
    const groupedByCycle = Array.from(cycleMap.values()).sort((a, b) => 
      a.cycleCode.localeCompare(b.cycleCode)
    );

    return {
      data: mappedData,
      groupedByCycle,
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch timeline controls:', error);
    throw error;
  }
};

/**
 * Get timeline controls for a specific cycle
 */
export const getTimelineControlsByCycle = async (
  cycleId: string,
  skip?: number,
  take?: number
): Promise<{ data: TimelineControl[]; totalCount: number }> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: skip || 0,
      take: take || 1000,
      requiresCounts: true,
    };

    const response = await apiRequest<TimelineGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/index-grid/timeline/by-cycle/${cycleId}`,
      requestBody,
      true
    );

    return {
      data: response.result.map(mapTimelineControl),
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch timeline controls by cycle:', error);
    throw error;
  }
};

/**
 * Create a new timeslot (auditor assignment)
 */
export const createTimeslot = async (request: CreateTimeslotRequest): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse, CreateTimeslotRequest>(
      'POST',
      '/audits/timeslot',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to create timeslot:', error);
    throw error;
  }
};

/**
 * Update an existing timeslot
 */
export const updateTimeslot = async (request: UpdateTimeslotRequest): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse, UpdateTimeslotRequest>(
      'PUT',
      '/audits/timeslot',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to update timeslot:', error);
    throw error;
  }
};

/**
 * Delete a timeslot
 */
export const deleteTimeslot = async (auditTimeslotId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/timeslot/${auditTimeslotId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete timeslot:', error);
    throw error;
  }
};

// ========================================
// Tab 2: Planning Documents API Functions
// ========================================

export interface GetPlanningDocumentsRequest {
  annualReportId: string;
  skip?: number;
  take?: number;
}

export interface GetPlanningDocumentsResponse {
  data: PlanningDocument[];
  totalCount: number;
}

/**
 * Get planning documents for an annual report
 */
export const getPlanningDocuments = async (
  request: GetPlanningDocumentsRequest
): Promise<GetPlanningDocumentsResponse> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
    };

    const response = await apiRequest<DocumentsGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/downloads/index-grid/${request.annualReportId}`,
      requestBody,
      true
    );

    return {
      data: response.result.map(mapPlanningDocument),
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch planning documents:', error);
    throw error;
  }
};

// ========================================
// Tab 3: Applications API Functions
// ========================================

export interface GetApplicationsRequest {
  annualReportId: string;
  skip?: number;
  take?: number;
  search?: SyncfusionSearchParam[];
  sorted?: { name: string; direction: 'ascending' | 'descending' }[];
}

export interface GetApplicationsResponse {
  data: Application[];
  totalCount: number;
}

/**
 * Get applications for an annual report
 */
export const getApplications = async (
  request: GetApplicationsRequest
): Promise<GetApplicationsResponse> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
      sorted: [{ name: 'Index', direction: 'ascending' as const }],
      ...(request.search && { search: request.search }),
    };

    if (request.sorted) {
      requestBody.sorted = request.sorted;
    }

    const response = await apiRequest<ApplicationsGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/index-grid/applications/${request.annualReportId}`,
      requestBody,
      true
    );

    return {
      data: response.result.map(app => mapApplication(app)),
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch applications:', error);
    throw error;
  }
};

/**
 * Create a new application
 */
export const createApplication = async (request: CreateApplicationRequest): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse, CreateApplicationRequest>(
      'POST',
      '/audits/application',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to create application:', error);
    throw error;
  }
};

/**
 * Update an existing application
 */
export const updateApplication = async (request: UpdateApplicationRequest): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse, UpdateApplicationRequest>(
      'PUT',
      '/audits/application',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to update application:', error);
    throw error;
  }
};

/**
 * Delete an application
 */
export const deleteApplication = async (auditApplicationId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/application/${auditApplicationId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete application:', error);
    throw error;
  }
};

// ========================================
// Tab 4: Change Indicator Checklist API Functions
// ========================================

export interface GetChangeIndicatorsRequest {
  annualReportId: string;
  skip?: number;
  take?: number;
}

export interface GetChangeIndicatorsResponse {
  data: ChangeIndicator[];
  totalCount: number;
}

/**
 * Get change indicators for an annual report
 */
export const getChangeIndicators = async (
  request: GetChangeIndicatorsRequest
): Promise<GetChangeIndicatorsResponse> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
    };

    const response = await apiRequest<ChangeIndicatorGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/index-grid/notifications/change-indicator/${request.annualReportId}`,
      requestBody,
      true
    );

    return {
      data: response.result.map(mapChangeIndicator),
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch change indicators:', error);
    throw error;
  }
};

/**
 * Mark change indicator as read
 */
export const markChangeIndicatorAsRead = async (notificationId: string): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'PUT',
      `/audits/change-indicator/read/${notificationId}`,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to mark change indicator as read:', error);
    throw error;
  }
};

/**
 * Delete change indicator
 */
export const deleteChangeIndicator = async (notificationId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/change-indicator/${notificationId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete change indicator:', error);
    throw error;
  }
};

export interface GetCertificationMessagesRequest {
  annualReportId: string;
  skip?: number;
  take?: number;
}

export interface GetCertificationMessagesResponse {
  data: CertificationMessage[];
  totalCount: number;
}

/**
 * Get certification messages for an annual report
 */
export const getCertificationMessages = async (
  request: GetCertificationMessagesRequest
): Promise<GetCertificationMessagesResponse> => {
  try {
    const requestBody: SyncfusionGridParams = {
      skip: request.skip || 0,
      take: request.take || 1000,
      requiresCounts: true,
    };

    const response = await apiRequest<CertificationMessageGridResponse, SyncfusionGridParams>(
      'POST',
      `/audits/index-grid/notifications/certification-comment/${request.annualReportId}`,
      requestBody,
      true
    );

    return {
      data: response.result.map(mapCertificationMessage),
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch certification messages:', error);
    throw error;
  }
};

/**
 * Mark certification message as read
 */
export const markCertificationMessageAsRead = async (notificationId: string): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'PUT',
      `/audits/certification-comment/read/${notificationId}`,
      undefined,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to mark certification message as read:', error);
    throw error;
  }
};

/**
 * Delete certification message
 */
export const deleteCertificationMessage = async (notificationId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/certification-comment/${notificationId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete certification message:', error);
    throw error;
  }
};

// ========================================
// Custom Planning Tasks (Planning Tasks Section)
// ========================================

/**
 * Backend types for custom planning tasks
 */
export interface CustomPlanBackend {
  Id: string;
  Name: string;
  CustomTimeSlots: CustomTimeslotBackend[];
  CreatedAt: string;
}

export interface CustomTimeslotBackend {
  CustomTimeslotId: string;
  AuditorId: string;
  AuditorName: string;
  StartDate: string;
  EndDate: string;
}

/**
 * Frontend types for custom planning tasks
 */
export interface CustomPlanTask {
  id: string;
  name: string;
  timeslots: {
    id: string;
    userId: string;
    userName: string;
    startDate: string;
    endDate: string;
  }[];
  createdAt: string;
}

/**
 * Get custom planning tasks (grid format)
 */
export const getCustomPlanningTasks = async (
  annualReportId: string,
  params: SyncfusionGridParams
): Promise<{ data: CustomPlanTask[]; totalCount: number }> => {
  try {
    const response = await apiRequest<{
      result: CustomPlanBackend[];
      count: number;
    }>(
      'POST',
      `/audits/index-grid/custom-timeline/${annualReportId}`,
      params,
      true
    );

    // Map backend data to frontend format
    const mappedTasks: CustomPlanTask[] = response.result.map(task => ({
      id: task.Id,
      name: task.Name,
      timeslots: task.CustomTimeSlots.map(ts => ({
        id: ts.CustomTimeslotId,
        userId: ts.AuditorId,
        userName: ts.AuditorName,
        startDate: ts.StartDate,
        endDate: ts.EndDate,
      })),
      createdAt: task.CreatedAt,
    }));

    return {
      data: mappedTasks,
      totalCount: response.count,
    };
  } catch (error) {
    console.error('Failed to fetch custom planning tasks:', error);
    throw error;
  }
};

/**
 * Create custom planning task
 */
export interface CreateCustomPlanRequest {
  annualReportId: string;
  name: string;
}

export const createCustomPlan = async (
  request: CreateCustomPlanRequest
): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'POST',
      '/audits/custom-plan',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to create custom plan:', error);
    throw error;
  }
};

/**
 * Update custom planning task
 */
export interface UpdateCustomPlanRequest {
  customPlanId: string;
  name: string;
}

export const updateCustomPlan = async (
  request: UpdateCustomPlanRequest
): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'PUT',
      '/audits/custom-plan',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to update custom plan:', error);
    throw error;
  }
};

/**
 * Delete custom planning task
 */
export const deleteCustomPlan = async (customPlanId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/custom-plan/${customPlanId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete custom plan:', error);
    throw error;
  }
};

/**
 * Create custom timeslot
 */
export interface CreateCustomTimeslotRequest {
  auditorId: string;
  customPlanId: string;
  startDate: string; // ISO date string
  endDate: string;   // ISO date string
}

export const createCustomTimeslot = async (
  request: CreateCustomTimeslotRequest
): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'POST',
      '/audits/custom-timeslot',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to create custom timeslot:', error);
    throw error;
  }
};

/**
 * Update custom timeslot
 */
export interface UpdateCustomTimeslotRequest {
  customTimeslotId: string;
  auditorId: string;
  startDate: string;
  endDate: string;
}

export const updateCustomTimeslot = async (
  request: UpdateCustomTimeslotRequest
): Promise<EmptyResponse> => {
  try {
    const response = await apiRequest<EmptyResponse>(
      'PUT',
      '/audits/custom-timeslot',
      request,
      true
    );
    return response;
  } catch (error) {
    console.error('Failed to update custom timeslot:', error);
    throw error;
  }
};

/**
 * Delete custom timeslot
 */
export const deleteCustomTimeslot = async (customTimeslotId: string): Promise<void> => {
  try {
    await apiRequest(
      'DELETE',
      `/audits/custom-timeslot/${customTimeslotId}`,
      undefined,
      true
    );
  } catch (error) {
    console.error('Failed to delete custom timeslot:', error);
    throw error;
  }
};
